import api, { multipartRequestWithProgress, STORAGE_KEYS } from './api';

/**
 * Offers Service
 * Handles all offer-related API calls for users
 */
const offersService = {
    exportMyOffers: async (format = 'xlsx') => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const token = localStorage.getItem('msk_user_access_token') || localStorage.getItem('msk_aqar_access_token');

        const response = await fetch(`${API_BASE_URL}/user/offers/export?format=${encodeURIComponent(format)}`, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
        });

        if (!response.ok) {
            throw new Error('Failed to export offers');
        }

        return response.blob();
    },

    /**
     * Get all offers with pagination and filters
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getAll: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/offers${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get my offers
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getMyOffers: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/offers${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get previous owner contacts for the current user
     * @returns {Promise<Object>}
     */
    getPreviousOwners: async (search = '') => {
        const params = search ? `?search=${encodeURIComponent(search)}` : '';
        return api.get(`/user/offers/previous-owners${params}`);
    },

    /**
     * Check if a similar offer already exists
     * @param {Object} params - { city, district, area, ownerPhone, ownerName }
     * @returns {Promise<{ isDuplicate: boolean, match: Object|null }>}
     */
    checkDuplicate: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/offers/check-duplicate${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get offer by ID
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    getById: async (id) => {
        return api.get(`/user/offers/${id}`);
    },

    /**
     * Create new offer with file uploads
     * @param {Object} data - Offer data
     * @param {Array<File>} imageFiles - Array of image files
     * @param {Array<File>} videoFiles - Array of video files
     * @param {File} coverImageFile - Cover image file
     * @returns {Promise<Object>}
     */
    create: async (data, imageFiles = [], videoFiles = [], coverImageFile = null, privateFiles = [], onProgress = null) => {
        // If no files, send as JSON
        if (imageFiles.length === 0 && videoFiles.length === 0 && !coverImageFile && privateFiles.length === 0) {
            return api.post('/user/offers', data);
        }

        // Use FormData for file uploads
        const formData = new FormData();

        // Add all data fields
        Object.keys(data).forEach(key => {
            const value = data[key];
            if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });

        // Add cover image file
        if (coverImageFile) {
            formData.append('coverImage', coverImageFile);
        }

        // Add image files
        imageFiles.forEach((file) => {
            formData.append('images', file);
        });

        // Add video files
        videoFiles.forEach((file) => {
            formData.append('videos', file);
        });

        // Add private files
        privateFiles.forEach((file) => {
            formData.append('privateFiles', file);
        });

        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        return multipartRequestWithProgress('POST', '/user/offers', formData, onProgress, token);
    },

    /**
     * Update offer with file uploads
     * @param {string} id - Offer ID
     * @param {Object} data - Updated offer data
     * @param {Array<File>} imageFiles - Array of new image files
     * @param {Array<File>} videoFiles - Array of new video files
     * @param {File} coverImageFile - New cover image file
     * @returns {Promise<Object>}
     */
    update: async (id, data, imageFiles = [], videoFiles = [], coverImageFile = null, privateFiles = [], onProgress = null) => {
        // Always remove status to prevent legacy enum validation errors
        delete data.status;
        // If no new files, send as JSON
        if (imageFiles.length === 0 && videoFiles.length === 0 && !coverImageFile && privateFiles.length === 0) {
            return api.put(`/user/offers/${id}`, data);
        }

        // Use FormData for file uploads
        const formData = new FormData();

        // Add all data fields
        Object.keys(data).forEach(key => {
            const value = data[key];
            if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });

        // Add cover image file
        if (coverImageFile) {
            formData.append('coverImage', coverImageFile);
        }

        // Add image files
        imageFiles.forEach((file) => {
            formData.append('images', file);
        });

        // Add video files
        videoFiles.forEach((file) => {
            formData.append('videos', file);
        });

        // Add private files
        privateFiles.forEach((file) => {
            formData.append('privateFiles', file);
        });

        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        return multipartRequestWithProgress('PUT', `/user/offers/${id}`, formData, onProgress, token);
    },

    /**
     * Duplicate an existing offer
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    duplicate: async (id) => {
        return api.post(`/user/offers/${id}/duplicate`);
    },

    /**
     * Delete offer
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    delete: async (id) => {
        return api.delete(`/user/offers/${id}`);
    },

    /**
     * Archive offer (soft delete) — kept for backward compatibility
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    archive: async (id) => {
        return api.patch(`/user/offers/${id}/archive`);
    },

    /**
     * Move offer to trash (recycle bin)
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    deleteOffer: async (id) => {
        return api.patch(`/user/offers/${id}/delete`);
    },

    /**
     * Restore offer from trash
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    restoreOffer: async (id) => {
        return api.post(`/user/offers/${id}/restore`);
    },

    /**
     * Get all trashed offers for the current user
     * @returns {Promise<Object>}
     */
    getDeletedOffers: async () => {
        return api.get('/user/offers/deleted');
    },

    /**
     * Change offer status
     * @param {string} id - Offer ID
     * @param {string} status - New status
     * @returns {Promise<Object>}
     */
    changeStatus: async (id, status) => {
        return api.patch(`/user/offers/${id}/status`, { status });
    },

    /**
     * Like an offer
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    like: async (id) => {
        return api.post(`/user/offers/${id}/like`);
    },

    /**
     * Unlike an offer
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    unlike: async (id) => {
        return api.delete(`/user/offers/${id}/like`);
    },

    /**
     * Add comment to offer
     * @param {string} id - Offer ID
     * @param {string} content - Comment content
     * @returns {Promise<Object>}
     */
    addComment: async (id, content) => {
        return api.post(`/user/offers/${id}/comments`, { content });
    },

    /**
     * Get offer comments
     * @param {string} id - Offer ID
     * @returns {Promise<Object>}
     */
    getComments: async (id) => {
        return api.get(`/user/offers/${id}/comments`);
    },

    /**
     * Analyze text using AI to extract offer data
     * @param {string} description - Free-text property description
     * @param {string} cityHint - Optional city hint to help AI
     * @returns {Promise<Object>}
     */
    analyzeWithAI: async (description, cityHint = '') => {
        return api.post('/user/offers/ai-create', { description, cityHint, autoCreate: false });
    },

    /**
     * Create offer from AI-analyzed data
     * @param {string} description - Free-text property description
     * @returns {Promise<Object>}
     */
    createWithAI: async (description) => {
        return api.post('/user/offers/ai-create', { description, autoCreate: true });
    },

    /**
     * Parse/Preview offers from Excel/CSV file (without creating)
     * @param {File} file - Excel or CSV file
     * @param {boolean} useAI - Whether to use AI for data formatting
     * @returns {Promise<Object>} - Parsed offers for preview
     */
    parseFile: async (file, useAI = false) => {
        const formData = new FormData();
        formData.append('file', file);
        const endpoint = useAI ? '/user/offers/parse?useAI=true' : '/user/offers/parse';
        return api.postMultipart(endpoint, formData);
    },

    /**
     * Create multiple offers after preview/editing
     * @param {Array} offers - Array of offer objects
     * @returns {Promise<Object>}
     */
    createBulk: async (offers) => {
        return api.post('/user/offers/create-bulk', { offers });
    },

    /**
     * Import offers from Excel/CSV file (direct import - deprecated, use parseFile + createBulk)
     * @param {File} file - Excel or CSV file
     * @param {boolean} useAI - Whether to use AI for data formatting
     * @returns {Promise<Object>}
     */
    importFromFile: async (file, useAI = false) => {
        const formData = new FormData();
        formData.append('file', file);
        const endpoint = useAI ? '/user/offers/import?useAI=true' : '/user/offers/import';
        return api.postMultipart(endpoint, formData);
    },

    /**
     * Download import template
     * @returns {Promise<Blob>}
     */
    downloadImportTemplate: async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1'}/user/offers/import-template`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('msk_aqar_access_token')}`
            }
        });
        if (!response.ok) throw new Error('Failed to download template');
        return response.blob();
    },

    /**
     * Toggle offer visibility in network
     * @param {string} id - Offer ID
     * @param {Array<string>} visibleFields - Fields to show in network (optional)
     * @returns {Promise<Object>}
     */
    toggleNetwork: async (id, visibleFields = null) => {
        if (visibleFields) {
            return api.patch(`/user/offers/${id}/network`, { visibleFields });
        }
        return api.patch(`/user/offers/${id}/network`);
    },

    /**
     * Update visible fields for network
     * @param {string} id - Offer ID
     * @param {Array<string>} visibleFields - Fields to show in network
     * @returns {Promise<Object>}
     */
    updateNetworkVisibility: async (id, visibleFields) => {
        return api.patch(`/user/offers/${id}/network`, { visibleFields });
    },

    /**
     * Update visible fields only (without toggling network status)
     * @param {string} id - Offer ID
     * @param {Array<string>} visibleFields - Fields to show in network
     * @returns {Promise<Object>}
     */
    updateVisibleFields: async (id, visibleFields) => {
        return api.patch(`/user/offers/${id}/visible-fields`, { visibleFields });
    },

    uploadPrivateImages: async (id, files = [], options = {}) => {
        const formData = new FormData();
        files.forEach((file) => formData.append('privateImages', file));

        if (options.visibilityLevel) {
            formData.append('visibilityLevel', options.visibilityLevel);
        }
        if (options.sharedWith) {
            formData.append('sharedWith', JSON.stringify(options.sharedWith));
        }

        return api.postMultipart(`/user/offers/${id}/private-images`, formData);
    },

    getPrivateImages: async (id, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/offers/${id}/private-images${queryString ? `?${queryString}` : ''}`);
    },

    getPrivateImageSignedUrl: async (offerId, imageId, expiresIn = 600) => {
        return api.get(`/user/offers/${offerId}/private-images/${imageId}/signed-url?expiresIn=${expiresIn}`);
    },

    deletePrivateImage: async (offerId, imageId) => {
        return api.delete(`/user/offers/${offerId}/private-images/${imageId}`);
    },

    sharePrivateImage: async (offerId, imageId, payload) => {
        return api.patch(`/user/offers/${offerId}/private-images/${imageId}/share`, payload);
    },

    /**
     * Get available fields for network visibility
     * @returns {Promise<Object>}
     */
    getNetworkFields: async () => {
        return api.get('/user/offers/network-fields');
    },

    /**
     * Analyze text using AI without authentication (public form)
     * @param {string} description - Free-text property description
     * @param {string} cityHint - Optional city hint
     * @returns {Promise<Object>}
     */
    analyzePublicWithAI: async (description, cityHint = '') => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const response = await fetch(`${API_BASE_URL}/public/offers/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ description, cityHint })
        });
        const result = await response.json();
        if (!response.ok) {
            const message = result?.error?.message || result?.message || 'فشل التحليل';
            throw new Error(message);
        }
        return result;
    },

    /**
     * Submit public offer (no auth required)
     * Supports multipart upload for coverImage, images[], videos[].
     * @param {Object} data - Offer data from public form
     * @param {Array<File>} imageFiles
     * @param {Array<File>} videoFiles
     * @param {File|null} coverImageFile
     * @returns {Promise<Object>}
     */
    submitPublicOffer: async (data, imageFiles = [], videoFiles = [], coverImageFile = null) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';

        const payload = {
            title: data.title || '',
            ownerName: data.ownerName,
            ownerPhone: data.ownerPhone,
            propertyType: data.propertyType || 'villa',
            offerType: data.offerType || 'sale',
            city: data.city,
            region: data.region || '',
            district: data.district || '',
            price: data.price || 0,
            priceOnRequest: data.priceOnRequest ? 'true' : 'false',
            area: data.area || '',
            rooms: data.rooms || '',
            bathrooms: data.bathrooms || '',
            age: data.age || '',
            direction: data.direction || '',
            streetWidth: data.streetWidth || '',
            floors: data.floors || '',
            deedType: data.deedType || '',
            acceptsPayments: data.acceptsPayments || 'no',
            offerNature: data.offerNature || 'owner',
            electricity: data.electricity || 'independent',
            water: data.water || 'independent',
            comment: data.comment || '',
            description: data.description || '',
            features: JSON.stringify(data.features || []),
            externalLinks: JSON.stringify(data.externalLinks || []),
            referralUserId: data.referralUserId || ''
        };

        const hasFiles = (imageFiles && imageFiles.length > 0) || (videoFiles && videoFiles.length > 0) || coverImageFile;

        let response;
        if (hasFiles) {
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    formData.append(key, value);
                }
            });
            if (coverImageFile) formData.append('coverImage', coverImageFile);
            (imageFiles || []).forEach(f => formData.append('images', f));
            (videoFiles || []).forEach(f => formData.append('videos', f));

            response = await fetch(`${API_BASE_URL}/public/offers`, {
                method: 'POST',
                body: formData
            });
        } else {
            response = await fetch(`${API_BASE_URL}/public/offers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        }

        const result = await response.json();
        if (!response.ok) {
            const message = result?.error?.message || result?.message || 'حدث خطأ في إرسال العرض';
            throw new Error(message);
        }
        return result;
    }
};

export default offersService;
